import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  dischargeSummaries: [],
  currentDischargeSummary: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all discharge summaries
export const fetchDischargeSummaries = createAsyncThunk(
  "dischargeSummary/fetchDischargeSummaries",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/discharge-summary/patient/${patientId}`
        : '/discharge-summary';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Discharge Summaries"
      );
    }
  }
);

// Fetch single discharge summary
export const fetchDischargeSummary = createAsyncThunk(
  "dischargeSummary/fetchDischargeSummary",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/discharge-summary/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Discharge Summary"
      );
    }
  }
);

// Create new discharge summary (with files)
export const createDischargeSummary = createAsyncThunk(
  "dischargeSummary/createDischargeSummary",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/discharge-summary',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data', // Force multipart
          },
        });
      return response.data;
    } catch (error) {
      if (error.response?.status === 422)
        return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || "Failed to create Discharge Summary"
      );
    }
  }
);

// Update discharge summary (with files)
export const updateDischargeSummary = createAsyncThunk(
  "dischargeSummary/updateDischargeSummary",
  async ({ case_id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/discharge-summary/case/${case_id}`,
        formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // Force multipart
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 422)
        return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || "Failed to update Discharge Summary"
      );
    }
  }
);

// Delete discharge summary
export const deleteDischargeSummary = createAsyncThunk(
  "dischargeSummary/deleteDischargeSummary",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/discharge-summary/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete Discharge Summary"
      );
    }
  }
);

// ==================== Slice ====================
const dischargeSummarySlice = createSlice({
  name: "dischargeSummary",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentDischargeSummary: (state, action) => {
      state.currentDischargeSummary = action.payload;
    },
    clearCurrentDischargeSummary: (state) => {
      state.currentDischargeSummary = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchDischargeSummaries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDischargeSummaries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dischargeSummaries = action.payload;
      })
      .addCase(fetchDischargeSummaries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchDischargeSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDischargeSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDischargeSummary = action.payload;
      })
      .addCase(fetchDischargeSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createDischargeSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDischargeSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        // action.payload ab {message: "...", data: {...}} hai
        // Isliye action.payload.data use karein state update ke liye
        state.dischargeSummaries.unshift(action.payload.data);
      })
      .addCase(createDischargeSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateDischargeSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDischargeSummary.fulfilled, (state, action) => {
        state.isLoading = false;

        // ✅ Extract data from the payload
        const updatedRecord = action.payload.data;

        const index = state.dischargeSummaries.findIndex(
          (d) => d.id === updatedRecord.id
        );

        if (index !== -1) {
          state.dischargeSummaries[index] = updatedRecord;
        }

        if (state.currentDischargeSummary?.id === updatedRecord.id) {
          state.currentDischargeSummary = updatedRecord;
        }
      })
      .addCase(updateDischargeSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteDischargeSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDischargeSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dischargeSummaries = state.dischargeSummaries.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentDischargeSummary?.id === action.payload) {
          state.currentDischargeSummary = null;
        }
      })
      .addCase(deleteDischargeSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentDischargeSummary,
  clearCurrentDischargeSummary,
} = dischargeSummarySlice.actions;

export default dischargeSummarySlice.reducer;